import { Coordinates } from '@collector/shared-ui-domain'
import PossessionAwayDangerousAttack from '@collector/shared-ui-domain/assets/rugby/possession-away-dangerous-attack.svg'
import PossessionAwayDefault from '@collector/shared-ui-domain/assets/rugby/possession-away-default.svg'
import PossessionAwayInPossession from '@collector/shared-ui-domain/assets/rugby/possession-away-in-possession.svg'
import PossessionHomeDangerousAttack from '@collector/shared-ui-domain/assets/rugby/possession-home-dangerous-attack.svg'
import PossessionHomeDefault from '@collector/shared-ui-domain/assets/rugby/possession-home-default.svg'
import PossessionHomeInPossession from '@collector/shared-ui-domain/assets/rugby/possession-home-in-possession.svg'
import {
  RugbyLeagueEventStatusId,
  RugbyLeagueIncidentId,
} from '@collector/sportsapi-client-legacy'

import {
  GetPossessionSvgOpts,
  IncidentsRequirePointingPositionConfig,
} from '../../../configuration/incidentsRequirePointingPositionConfig'
import {
  defaultCoordinatesTransform,
  FIELD_LENGTH,
  getInPossessionPossessionSvg,
  isWithinRedZone,
  isWithinRedZones,
  isWithinTryArea,
  isWithinTryAreas,
  LINE_THRESHOLD,
  TRY_AREA_X_COORDINATE_THRESHOLD,
} from '../../configuration'

export const incidentsRequirePointingPositionConfig: IncidentsRequirePointingPositionConfig<
  RugbyLeagueIncidentId,
  RugbyLeagueEventStatusId
> = {
  pointingPositionIncidents: {
    [RugbyLeagueIncidentId.Try]: {
      transformCoordinates: (coordinates, teamSide) => {
        let x: number
        if (isWithinTryArea(teamSide, coordinates.x)) {
          x = Math.round(coordinates.x)
        } else {
          x =
            teamSide === 'home'
              ? Math.round(FIELD_LENGTH - TRY_AREA_X_COORDINATE_THRESHOLD)
              : Math.round(TRY_AREA_X_COORDINATE_THRESHOLD)
        }
        const y = coordinates.y

        return { x, y }
      },
    },
    [RugbyLeagueIncidentId.ConversionAttempt]: {
      transformCoordinates: (coordinates, teamSide) =>
        defaultCoordinatesTransform(coordinates, teamSide),
    },
    [RugbyLeagueIncidentId.PenaltyAwarded]: {
      transformCoordinates: (coordinates, teamSide) =>
        defaultCoordinatesTransform(coordinates, teamSide),
    },
    [RugbyLeagueIncidentId.DropGoal]: {
      transformCoordinates: (coordinates, teamSide) =>
        defaultCoordinatesTransform(coordinates, teamSide),
    },
    [RugbyLeagueIncidentId.DropGoalMissed]: {
      transformCoordinates: (coordinates, teamSide) =>
        defaultCoordinatesTransform(coordinates, teamSide),
    },
    [RugbyLeagueIncidentId.Scrum]: {
      transformCoordinates: (coordinates, teamSide) =>
        defaultCoordinatesTransform(coordinates, teamSide),
    },
    [RugbyLeagueIncidentId.Lineout]: {
      transformCoordinates: ({ x, y }) => {
        const minX = TRY_AREA_X_COORDINATE_THRESHOLD
        const maxX =
          FIELD_LENGTH - TRY_AREA_X_COORDINATE_THRESHOLD - LINE_THRESHOLD
        const clampedX = Math.round(Math.max(minX, Math.min(x, maxX)))

        return {
          x: clampedX,
          y: y > 50 ? FIELD_LENGTH : 1,
        }
      },
    },
  },
  excludedIncidentEventStatuses: [
    RugbyLeagueEventStatusId.Halftime,
    RugbyLeagueEventStatusId.ExtratimeHalftime,
    RugbyLeagueEventStatusId.WaitingForPenalty,
  ],
  getIncidentIdForCoordinateX(
    event: MouseEvent,
    coordinate: Coordinates['x'],
  ): RugbyLeagueIncidentId | null {
    if (event.type === 'contextmenu') {
      if (!isWithinTryAreas(coordinate)) return RugbyLeagueIncidentId.TackleMade
    } else {
      if (isWithinRedZones(coordinate)) return RugbyLeagueIncidentId.EntryInto20
      else return RugbyLeagueIncidentId.InPossession
    }
    return null
  },
  getPossessionSvg({ isAway }: GetPossessionSvgOpts, incidentId, coordinate) {
    switch (incidentId) {
      case RugbyLeagueIncidentId.TackleMade:
        return getTackleMadePossessionSvg(coordinate, isAway)
      case RugbyLeagueIncidentId.InPossession:
        return getInPossessionPossessionSvg(coordinate, isAway)
      case RugbyLeagueIncidentId.EntryInto20:
        return isAway
          ? PossessionAwayDangerousAttack
          : PossessionHomeDangerousAttack
      default:
        return isAway ? PossessionAwayDefault : PossessionHomeDefault
    }
  },
}

function getTackleMadePossessionSvg(
  coordinate?: Coordinates['x'],
  isAway?: boolean,
): string {
  if (coordinate === undefined)
    return isAway ? PossessionAwayDefault : PossessionHomeDefault
  const teamSide = isAway ? 'away' : 'home'
  if (isWithinRedZone(teamSide, coordinate))
    return isAway
      ? PossessionAwayDangerousAttack
      : PossessionHomeDangerousAttack

  return isAway ? PossessionAwayInPossession : PossessionHomeInPossession
}
